import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  UnderlineType,
  BorderStyle,
  PageBreak,
} from 'docx'
import { IProjectSource } from '../../../api/api_hooks/project-sources'

export interface ExportOptions {
  projectTitle: string
  sources: IProjectSource[]
  includeSections: string[]
  format: 'markdown' | 'word'
}

/**
 * Helper function to parse JSON data into readable text
 */
function parseJsonToText(data: any, depth: number = 0): string {
  if (!data) return ''

  if (typeof data === 'string') return data

  if (typeof data === 'number' || typeof data === 'boolean') {
    return String(data)
  }

  if (Array.isArray(data)) {
    return data.map(item => parseJsonToText(item, depth)).filter(Boolean).join('; ')
  }

  if (typeof data === 'object') {
    // Check for common fields that should be displayed nicely
    if (data.name) return data.name
    if (data.description) return data.description
    if (data.value !== undefined) return String(data.value)
    if (data.text) return data.text
    if (data.title) return data.title

    // For objects with key-value pairs, format them nicely
    const entries = Object.entries(data)
      .filter(([key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        const formattedKey = key.replace(/([A-Z])/g, ' $1').trim()
        const formattedValue = parseJsonToText(value, depth + 1)
        if (formattedValue) {
          return `${formattedKey}: ${formattedValue}`
        }
        return null
      })
      .filter(Boolean)

    return entries.join(', ')
  }

  return ''
}

/**
 * Generate Markdown content from project sources
 */
export function generateMarkdownContent(options: ExportOptions): string {
  const { projectTitle, sources, includeSections } = options

  let markdown = `# ${projectTitle}\n\n`
  markdown += `*Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}*\n\n`
  markdown += `---\n\n`

  // Table of contents
  markdown += `## Table des matières\n\n`
  if (includeSections.includes('sources')) markdown += `- [Sources](#sources)\n`
  if (includeSections.includes('entities')) markdown += `- [Entités biologiques](#entités-biologiques)\n`
  if (includeSections.includes('protocols')) markdown += `- [Protocoles expérimentaux](#protocoles-expérimentaux)\n`
  if (includeSections.includes('methods')) markdown += `- [Méthodes](#méthodes)\n`
  if (includeSections.includes('results')) markdown += `- [Résultats & Statistiques](#résultats--statistiques)\n`
  markdown += `\n---\n\n`

  // Sources section
  if (includeSections.includes('sources')) {
    markdown += `## Sources\n\n`
    markdown += `Total: ${sources.length} source${sources.length > 1 ? 's' : ''}\n\n`

    sources.forEach((source, index) => {
      markdown += `### ${index + 1}. ${source.title}\n\n`

      if (source.metadata) {
        if (source.metadata.authors) {
          markdown += `**Auteurs:** ${Array.isArray(source.metadata.authors) ? source.metadata.authors.join(', ') : source.metadata.authors}\n\n`
        }
        if (source.metadata.journal) {
          markdown += `**Journal:** ${source.metadata.journal}\n\n`
        }
        if (source.metadata.publicationDate) {
          markdown += `**Date de publication:** ${source.metadata.publicationDate}\n\n`
        }
        if (source.metadata.doi) {
          markdown += `**DOI:** ${source.metadata.doi}\n\n`
        }
        if (source.url) {
          markdown += `**URL:** [${source.url}](${source.url})\n\n`
        }
      }

      if (source.metadata?.abstract || source.parsedContent) {
        markdown += `**Résumé:**\n\n${source.metadata?.abstract || source.parsedContent?.substring(0, 500)}...\n\n`
      }

      markdown += `---\n\n`
    })
  }

  // Entities section
  if (includeSections.includes('entities')) {
    markdown += `## Entités biologiques\n\n`

    const allEntities = {
      pathologies: new Set<string>(),
      models: new Set<string>(),
      molecules: new Set<string>(),
      cells: new Set<string>(),
    }

    sources.forEach((source) => {
      if (source.extractedEntities?.entities) {
        const entities = source.extractedEntities.entities
        entities.pathologies?.forEach((p) => allEntities.pathologies.add(p))
        entities.models?.forEach((m) => allEntities.models.add(m))
        entities.molecules?.forEach((m) => allEntities.molecules.add(m))
        entities.cells?.forEach((c) => allEntities.cells.add(c))
      }
    })

    if (allEntities.pathologies.size > 0) {
      markdown += `### Pathologies\n\n`
      Array.from(allEntities.pathologies).forEach((p) => {
        markdown += `- ${p}\n`
      })
      markdown += `\n`
    }

    if (allEntities.models.size > 0) {
      markdown += `### Modèles\n\n`
      Array.from(allEntities.models).forEach((m) => {
        markdown += `- ${m}\n`
      })
      markdown += `\n`
    }

    if (allEntities.molecules.size > 0) {
      markdown += `### Molécules\n\n`
      Array.from(allEntities.molecules).forEach((m) => {
        markdown += `- ${m}\n`
      })
      markdown += `\n`
    }

    if (allEntities.cells.size > 0) {
      markdown += `### Cellules\n\n`
      Array.from(allEntities.cells).forEach((c) => {
        markdown += `- ${c}\n`
      })
      markdown += `\n`
    }

    markdown += `---\n\n`
  }

  // Protocols section
  if (includeSections.includes('protocols')) {
    markdown += `## Protocoles expérimentaux\n\n`

    sources.forEach((source) => {
      if (source.extractedEntities?.protocols && source.extractedEntities.protocols.length > 0) {
        markdown += `### ${source.title}\n\n`
        source.extractedEntities.protocols.forEach((protocol, idx) => {
          markdown += `#### Protocole ${idx + 1}\n\n`
          const text = parseJsonToText(protocol)
          markdown += `${text || 'Données non disponibles'}\n\n`
        })
      }
    })

    markdown += `---\n\n`
  }

  // Methods section
  if (includeSections.includes('methods')) {
    markdown += `## Méthodes\n\n`

    sources.forEach((source) => {
      if (source.extractedEntities?.methods && source.extractedEntities.methods.length > 0) {
        markdown += `### ${source.title}\n\n`
        source.extractedEntities.methods.forEach((method, idx) => {
          markdown += `#### Méthode ${idx + 1}\n\n`
          const text = parseJsonToText(method)
          markdown += `${text || 'Données non disponibles'}\n\n`
        })
      }
    })

    markdown += `---\n\n`
  }

  // Results section
  if (includeSections.includes('results')) {
    markdown += `## Résultats & Statistiques\n\n`

    sources.forEach((source) => {
      if (source.extractedEntities?.results && source.extractedEntities.results.length > 0) {
        markdown += `### ${source.title}\n\n`
        source.extractedEntities.results.forEach((result, idx) => {
          markdown += `#### Résultat ${idx + 1}\n\n`
          const text = parseJsonToText(result)
          markdown += `${text || 'Données non disponibles'}\n\n`
        })
      }

      if (source.extractedEntities?.statistics && source.extractedEntities.statistics.length > 0) {
        markdown += `#### Statistiques\n\n`
        source.extractedEntities.statistics.forEach((stat, idx) => {
          const text = parseJsonToText(stat)
          markdown += `**Stat ${idx + 1}:** ${text || 'Données non disponibles'}\n\n`
        })
      }
    })

    markdown += `---\n\n`
  }

  // Footer
  markdown += `\n\n*Document généré automatiquement par MedInsight - Careflai*\n`

  return markdown
}

/**
 * Generate Word document from project sources with professional medical formatting
 */
export async function generateWordDocument(options: ExportOptions): Promise<Blob> {
  const { projectTitle, sources, includeSections } = options

  const children: Paragraph[] = []

  // ===== COVER PAGE =====
  children.push(
    new Paragraph({
      text: '',
      spacing: { before: 4000 },
    })
  )

  children.push(
    new Paragraph({
      text: projectTitle.toUpperCase(),
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
      spacing: { after: 800 },
      border: {
        bottom: {
          color: '7C3AED',
          space: 20,
          style: BorderStyle.SINGLE,
          size: 24,
        },
      },
    })
  )

  children.push(
    new Paragraph({
      text: 'RAPPORT DE SYNTHÈSE SCIENTIFIQUE',
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      style: 'Subtitle',
    })
  )

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Généré le ${new Date().toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}`,
          italics: true,
          color: '666666',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    })
  )

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `à ${new Date().toLocaleTimeString('fr-FR')}`,
          italics: true,
          color: '666666',
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 2000 },
    })
  )

  // Summary box on cover
  children.push(
    new Paragraph({
      text: 'RÉSUMÉ DU PROJET',
      alignment: AlignmentType.CENTER,
      spacing: { before: 1000, after: 400 },
      border: {
        top: { color: '7C3AED', space: 10, style: BorderStyle.DOUBLE, size: 6 },
        bottom: { color: '7C3AED', space: 10, style: BorderStyle.DOUBLE, size: 6 },
        left: { color: '7C3AED', space: 10, style: BorderStyle.DOUBLE, size: 6 },
        right: { color: '7C3AED', space: 10, style: BorderStyle.DOUBLE, size: 6 },
      },
    })
  )

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Nombre de sources: `,
          bold: true,
        }),
        new TextRun({
          text: `${sources.length}`,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
    })
  )

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Sections incluses: `,
          bold: true,
        }),
        new TextRun({
          text: `${includeSections.length}`,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    })
  )

  // Page break after cover
  children.push(
    new Paragraph({
      children: [new PageBreak()],
    })
  )

  // ===== TABLE OF CONTENTS =====
  children.push(
    new Paragraph({
      text: 'TABLE DES MATIÈRES',
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 200, after: 400 },
      border: {
        bottom: {
          color: '7C3AED',
          space: 10,
          style: BorderStyle.SINGLE,
          size: 12,
        },
      },
    })
  )

  let sectionNumber = 1
  if (includeSections.includes('sources')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true }),
          new TextRun('Sources'),
        ],
        spacing: { after: 150 },
        indent: { left: 400 },
      })
    )
    sectionNumber++
  }
  if (includeSections.includes('entities')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true }),
          new TextRun('Entités biologiques'),
        ],
        spacing: { after: 150 },
        indent: { left: 400 },
      })
    )
    sectionNumber++
  }
  if (includeSections.includes('protocols')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true }),
          new TextRun('Protocoles expérimentaux'),
        ],
        spacing: { after: 150 },
        indent: { left: 400 },
      })
    )
    sectionNumber++
  }
  if (includeSections.includes('methods')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true }),
          new TextRun('Méthodes'),
        ],
        spacing: { after: 150 },
        indent: { left: 400 },
      })
    )
    sectionNumber++
  }
  if (includeSections.includes('results')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true }),
          new TextRun('Résultats & Statistiques'),
        ],
        spacing: { after: 150 },
        indent: { left: 400 },
      })
    )
    sectionNumber++
  }

  // Page break after TOC
  children.push(
    new Paragraph({
      children: [new PageBreak()],
    })
  )

  // ===== SOURCES SECTION =====
  sectionNumber = 1
  if (includeSections.includes('sources')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true, size: 32 }),
          new TextRun({ text: 'SOURCES', bold: true, size: 32 }),
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 300 },
        border: {
          bottom: {
            color: '7C3AED',
            space: 10,
            style: BorderStyle.SINGLE,
            size: 12,
          },
        },
      })
    )
    sectionNumber++

    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: 'Nombre total de sources: ', bold: true }),
          new TextRun({ text: `${sources.length}`, color: '7C3AED', bold: true }),
        ],
        spacing: { after: 400 },
      })
    )

    sources.forEach((source, index) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${index + 1}. `, bold: true, size: 28 }),
            new TextRun({ text: source.title, bold: true, size: 28 }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      )

      if (source.metadata) {
        if (source.metadata.authors) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: '👤 Auteurs: ', bold: true }),
                new TextRun({
                  text: Array.isArray(source.metadata.authors)
                    ? source.metadata.authors.join(', ')
                    : source.metadata.authors,
                  italics: true,
                }),
              ],
              spacing: { after: 100 },
              indent: { left: 400 },
            })
          )
        }

        if (source.metadata.journal) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: '📖 Journal: ', bold: true }),
                new TextRun({ text: source.metadata.journal, italics: true }),
              ],
              spacing: { after: 100 },
              indent: { left: 400 },
            })
          )
        }

        if (source.metadata.publicationDate) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: '📅 Date: ', bold: true }),
                new TextRun(source.metadata.publicationDate),
              ],
              spacing: { after: 100 },
              indent: { left: 400 },
            })
          )
        }

        if (source.metadata.doi) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: '🔗 DOI: ', bold: true }),
                new TextRun({
                  text: source.metadata.doi,
                  color: '0000FF',
                  underline: { type: UnderlineType.SINGLE },
                }),
              ],
              spacing: { after: 100 },
              indent: { left: 400 },
            })
          )
        }

        if (source.url) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: '🌐 URL: ', bold: true }),
                new TextRun({
                  text: source.url,
                  color: '0000FF',
                  underline: { type: UnderlineType.SINGLE },
                }),
              ],
              spacing: { after: 200 },
              indent: { left: 400 },
            })
          )
        }
      }

      if (source.metadata?.abstract || source.parsedContent) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: '📄 Résumé:', bold: true, size: 24 })],
            spacing: { before: 200, after: 100 },
            indent: { left: 400 },
          })
        )
        const abstract = source.metadata?.abstract || source.parsedContent?.substring(0, 800)
        children.push(
          new Paragraph({
            text: abstract + (abstract && abstract.length > 800 ? '...' : ''),
            spacing: { after: 300 },
            indent: { left: 720 },
            alignment: AlignmentType.JUSTIFIED,
          })
        )
      }
    })

    // Page break after sources
    children.push(
      new Paragraph({
        children: [new PageBreak()],
      })
    )
  }

  // ===== ENTITIES SECTION =====
  if (includeSections.includes('entities')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true, size: 32 }),
          new TextRun({ text: 'ENTITÉS BIOLOGIQUES', bold: true, size: 32 }),
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 300 },
        border: {
          bottom: {
            color: '7C3AED',
            space: 10,
            style: BorderStyle.SINGLE,
            size: 12,
          },
        },
      })
    )
    sectionNumber++

    const allEntities = {
      pathologies: new Set<string>(),
      models: new Set<string>(),
      molecules: new Set<string>(),
      cells: new Set<string>(),
    }

    sources.forEach((source) => {
      if (source.extractedEntities?.entities) {
        const entities = source.extractedEntities.entities
        entities.pathologies?.forEach((p) => allEntities.pathologies.add(p))
        entities.models?.forEach((m) => allEntities.models.add(m))
        entities.molecules?.forEach((m) => allEntities.molecules.add(m))
        entities.cells?.forEach((c) => allEntities.cells.add(c))
      }
    })

    if (allEntities.pathologies.size > 0) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: '🦠 ', size: 24 }),
            new TextRun({ text: 'Pathologies identifiées', bold: true, size: 24 }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      )
      Array.from(allEntities.pathologies).forEach((p) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '• ', color: '7C3AED', bold: true }),
              new TextRun(p),
            ],
            spacing: { after: 100 },
            indent: { left: 720 },
          })
        )
      })
    }

    if (allEntities.models.size > 0) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: '🔬 ', size: 24 }),
            new TextRun({ text: 'Modèles expérimentaux', bold: true, size: 24 }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      )
      Array.from(allEntities.models).forEach((m) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '• ', color: '7C3AED', bold: true }),
              new TextRun(m),
            ],
            spacing: { after: 100 },
            indent: { left: 720 },
          })
        )
      })
    }

    if (allEntities.molecules.size > 0) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: '⚗️ ', size: 24 }),
            new TextRun({ text: 'Molécules et composés', bold: true, size: 24 }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      )
      Array.from(allEntities.molecules).forEach((m) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '• ', color: '7C3AED', bold: true }),
              new TextRun(m),
            ],
            spacing: { after: 100 },
            indent: { left: 720 },
          })
        )
      })
    }

    if (allEntities.cells.size > 0) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: '🧬 ', size: 24 }),
            new TextRun({ text: 'Types cellulaires', bold: true, size: 24 }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        })
      )
      Array.from(allEntities.cells).forEach((c) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '• ', color: '7C3AED', bold: true }),
              new TextRun(c),
            ],
            spacing: { after: 100 },
            indent: { left: 720 },
          })
        )
      })
    }

    // Page break after entities
    children.push(
      new Paragraph({
        children: [new PageBreak()],
      })
    )
  }

  // ===== PROTOCOLS SECTION =====
  if (includeSections.includes('protocols')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true, size: 32 }),
          new TextRun({ text: 'PROTOCOLES EXPÉRIMENTAUX', bold: true, size: 32 }),
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 300 },
        border: {
          bottom: {
            color: '7C3AED',
            space: 10,
            style: BorderStyle.SINGLE,
            size: 12,
          },
        },
      })
    )
    sectionNumber++

    sources.forEach((source) => {
      if (source.extractedEntities?.protocols && source.extractedEntities.protocols.length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '📋 ', size: 24 }),
              new TextRun({ text: source.title, bold: true, size: 24 }),
            ],
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        )

        source.extractedEntities.protocols.forEach((protocol, idx) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `Protocole ${idx + 1}`, bold: true, underline: {} }),
              ],
              spacing: { before: 200, after: 100 },
              indent: { left: 400 },
            })
          )

          const protocolText = parseJsonToText(protocol)
          if (protocolText) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: '• ', color: '7C3AED', bold: true }),
                  new TextRun(protocolText),
                ],
                spacing: { after: 200 },
                indent: { left: 720 },
                alignment: AlignmentType.JUSTIFIED,
              })
            )
          } else {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: 'Données non disponibles', italics: true, color: '999999' }),
                ],
                spacing: { after: 200 },
                indent: { left: 720 },
              })
            )
          }
        })
      }
    })

    // Page break after protocols
    children.push(
      new Paragraph({
        children: [new PageBreak()],
      })
    )
  }

  // ===== METHODS SECTION =====
  if (includeSections.includes('methods')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true, size: 32 }),
          new TextRun({ text: 'MÉTHODES', bold: true, size: 32 }),
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 300 },
        border: {
          bottom: {
            color: '7C3AED',
            space: 10,
            style: BorderStyle.SINGLE,
            size: 12,
          },
        },
      })
    )
    sectionNumber++

    sources.forEach((source) => {
      if (source.extractedEntities?.methods && source.extractedEntities.methods.length > 0) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '🔍 ', size: 24 }),
              new TextRun({ text: source.title, bold: true, size: 24 }),
            ],
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        )

        source.extractedEntities.methods.forEach((method, idx) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `Méthode ${idx + 1}`, bold: true, underline: {} }),
              ],
              spacing: { before: 200, after: 100 },
              indent: { left: 400 },
            })
          )

          const methodText = parseJsonToText(method)
          if (methodText) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: '• ', color: '7C3AED', bold: true }),
                  new TextRun(methodText),
                ],
                spacing: { after: 200 },
                indent: { left: 720 },
                alignment: AlignmentType.JUSTIFIED,
              })
            )
          } else {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: 'Données non disponibles', italics: true, color: '999999' }),
                ],
                spacing: { after: 200 },
                indent: { left: 720 },
              })
            )
          }
        })
      }
    })

    // Page break after methods
    children.push(
      new Paragraph({
        children: [new PageBreak()],
      })
    )
  }

  // ===== RESULTS SECTION =====
  if (includeSections.includes('results')) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({ text: `${sectionNumber}. `, bold: true, size: 32 }),
          new TextRun({ text: 'RÉSULTATS & STATISTIQUES', bold: true, size: 32 }),
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 400, after: 300 },
        border: {
          bottom: {
            color: '7C3AED',
            space: 10,
            style: BorderStyle.SINGLE,
            size: 12,
          },
        },
      })
    )
    sectionNumber++

    sources.forEach((source) => {
      if (
        (source.extractedEntities?.results && source.extractedEntities.results.length > 0) ||
        (source.extractedEntities?.statistics && source.extractedEntities.statistics.length > 0)
      ) {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: '📊 ', size: 24 }),
              new TextRun({ text: source.title, bold: true, size: 24 }),
            ],
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        )

        source.extractedEntities?.results?.forEach((result, idx) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `Résultat ${idx + 1}`, bold: true, underline: {} }),
              ],
              spacing: { before: 200, after: 100 },
              indent: { left: 400 },
            })
          )

          const resultText = parseJsonToText(result)
          if (resultText) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: '• ', color: '7C3AED', bold: true }),
                  new TextRun(resultText),
                ],
                spacing: { after: 200 },
                indent: { left: 720 },
                alignment: AlignmentType.JUSTIFIED,
              })
            )
          } else {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: 'Données non disponibles', italics: true, color: '999999' }),
                ],
                spacing: { after: 200 },
                indent: { left: 720 },
              })
            )
          }
        })

        if (source.extractedEntities?.statistics && source.extractedEntities.statistics.length > 0) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: '📈 Statistiques', bold: true, underline: {} }),
              ],
              spacing: { before: 200, after: 100 },
              indent: { left: 400 },
            })
          )
          source.extractedEntities.statistics.forEach((stat, idx) => {
            const statText = parseJsonToText(stat)
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: '• ', color: '7C3AED', bold: true }),
                  new TextRun(statText || 'Données non disponibles'),
                ],
                spacing: { after: 100 },
                indent: { left: 720 },
              })
            )
          })
        }
      }
    })
  }

  // ===== FOOTER =====
  children.push(
    new Paragraph({
      text: '',
      spacing: { before: 2000 },
    })
  )

  children.push(
    new Paragraph({
      text: '',
      border: {
        top: {
          color: '7C3AED',
          space: 10,
          style: BorderStyle.SINGLE,
          size: 12,
        },
      },
      spacing: { before: 400, after: 200 },
    })
  )

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Document généré automatiquement par MedInsight — Plateforme Careflai',
          italics: true,
          color: '666666',
          size: 18,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
    })
  )

  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Ce rapport est à but informatif et ne remplace pas l\'expertise médicale professionnelle',
          italics: true,
          color: '999999',
          size: 16,
        }),
      ],
      alignment: AlignmentType.CENTER,
    })
  )

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        children: children,
      },
    ],
  })

  return await Packer.toBlob(doc)
}

/**
 * Download a file to the user's computer
 */
export function downloadFile(content: Blob | string, filename: string, mimeType: string) {
  const blob = typeof content === 'string' ? new Blob([content], { type: mimeType }) : content

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Main export function that handles all formats
 */
export async function exportProject(options: ExportOptions): Promise<void> {
  const { format, projectTitle } = options
  const timestamp = new Date().toISOString().split('T')[0]
  const sanitizedTitle = projectTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()

  if (format === 'markdown') {
    const content = generateMarkdownContent(options)
    downloadFile(content, `${sanitizedTitle}_${timestamp}.md`, 'text/markdown')
  } else if (format === 'word') {
    const blob = await generateWordDocument(options)
    downloadFile(
      blob,
      `${sanitizedTitle}_${timestamp}.docx`,
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    )
  } else {
    throw new Error(`Unsupported format: ${format}`)
  }
}
